// Motor de análisis: Carpeta Tributaria + Certificado TGR + parámetros (+ datos del ERP) → modelo del informe
import { parseDMY } from "./parser";
import { construirE, validaciones } from "./contrato";

export const MES_NOMBRE = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAY = 86400000;
const IVA = 0.19;
const n = (v) => (v === "" || v === null || v === undefined || isNaN(+v) ? 0 : +v);
const has = (v) => !(v === "" || v === null || v === undefined || isNaN(+v));

/** Código 115: tasa PPM en puntos % (0.125 = 0,125%). Corrige "0.125" mal leído como miles → 125. */
export function normalizeTasaPPM(v) {
  if (!has(v)) return null;
  const x = +v;
  if (x >= 10 && Number.isInteger(x)) {
    const fixed = x / 1000;
    if (fixed > 0 && fixed < 10) return fixed;
  }
  return x;
}

// Tasas de retención de honorarios (Ley 21.133), alineadas con hon_calculos.py
export const RET_HON = {
  2020: 10.75, 2021: 11.5, 2022: 12.25, 2023: 13.0,
  2024: 13.75, 2025: 14.5, 2026: 15.25, 2027: 16, 2028: 17,
};
export function tasaRetencionHonorarios(anio) {
  const a = +anio;
  if (a <= 2020) return 10.75;
  if (a >= 2028) return 17;
  return RET_HON[a] || 15.25;
}
// Interés penal vigente 2° semestre 2026 (Res. Ex. SII N°88/2026): 7,70% anual, aplicado por día (/360)
export const TASA_INTERES_ANUAL = 7.7;
// UF de respaldo (07-10-2026) para cuando no se pudo consultar el valor del día
const UF_RESPALDO = 41114.54;
export const interesDiario = (P) => (has(P?.tasaInteresAnual) && P.tasaInteresAnual !== "" ? +P.tasaInteresAnual : TASA_INTERES_ANUAL) / 100 / 360;

export const clp = (v) => (v < 0 ? "-" : "") + "$" + Math.round(Math.abs(v || 0)).toLocaleString("es-CL");
export const fmtPct = (v, d = 3) => {
  const x = +v;
  return (Number.isInteger(x) ? x.toString() : x.toFixed(d).replace(/0+$/, "").replace(/\.$/, "")).replace(".", ",") + "%";
};
export const fmtDate = (d) =>
  d ? `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}` : "—";

export function regimenKey(reg = "") {
  const r = (reg || "").toUpperCase();
  if (/TRANSPARENTE|14 ?D ?N?°? ?8/.test(r)) return "14D8";
  if (/PRO ?PYME/.test(r)) return "14D3";
  if (/SEMI|14 ?A|GENERAL/.test(r)) return "14A";
  return "14D3";
}

export function defaultParams(C) {
  const corte = parseDMY(C?.fechaGeneracion) || new Date();
  const declared = (C?.f29 || []).filter((f) => f.declarado && f.codes && (f.codes["563"] || f.codes["538"]));
  const anio = declared.length ? Math.max(...declared.map((f) => f.anio)) : corte.getFullYear();
  const rk = regimenKey(C?.regimen);
  const tasaIDPC = rk === "14D3" ? (anio <= 2027 ? 12.5 : anio === 2028 ? 15 : 25) : rk === "14D8" ? 0 : 27;
  const inicio = parseDMY(C?.inicioActividades);
  const anioInicio = inicio && inicio.getFullYear() === anio;
  const ppmLegal = rk === "14D3" ? (anio <= 2027 ? 0.125 : 0.25) : rk === "14D8" ? 0.2 : 1;
  const lastRate = normalizeTasaPPM([...declared].sort((a, b) => b.periodo - a.periodo)[0]?.codes?.["115"]);
  const pad = (x) => String(x).padStart(2, "0");
  return {
    estudio: "AEQUOR GROUP", asesor: "",
    fechaCorte: `${corte.getFullYear()}-${pad(corte.getMonth() + 1)}-${pad(corte.getDate())}`, anio,
    tasaIDPC, ppmLegal, anioInicio: !!anioInicio, tasaRetHon: tasaRetencionHonorarios(anio),
    tasaPPMFutura: lastRate ?? ppmLegal, uf: "", mesesPromedio: 6,
    tasaInteresAnual: TASA_INTERES_ANUAL, ipc: null, ipcProyectado: 0, indicadores: null,
    afComoGasto: rk !== "14A",
    fuenteHonorarios: "f29",
    ultimoPeriodoCentralizado: "", excluirImpagas: false,
    gastosSinIVA: 0, remuneraciones: 0, agregados: 0, deducciones: 0, otrosCreditos: 0,
    pct14E: rk === "14D3" ? 50 : 0, retirosEstimados: 0, compraAFPlan: 0, gastosAnticipados: 0,
    caja: "", clientes: "", activoFijoNeto: "", proveedores: "", prestamos: "", capitalPagado: "", ventasContables: "", gastosContables: "",
    lecturaAsesor: "", comentarioFinal: "", notasInternas: "",
  };
}

const shiftWeekend = (d) => {
  const w = d.getDay();
  if (w === 6) return new Date(d.getTime() + 2 * DAY);
  if (w === 0) return new Date(d.getTime() + DAY);
  return d;
};
export const vencF29 = (anio, mes) => shiftWeekend(new Date(anio, mes, 20));
export const vencPostergado = (anio, mes) => shiftWeekend(new Date(anio, mes + 2, 20));
const mesKey = (anio, m) => `${anio}-${String(m).padStart(2, "0")}`;

// Reajuste de PPM (art. 95 LIR): variación del IPC entre el mes anterior al pago y noviembre; nunca negativo.
// El PPM de un mes se paga el mes siguiente, así que se acumula el IPC desde ese mes de pago hasta noviembre.
// Sin IPC cargado (informes antiguos) se mantiene la aproximación de 1%.
export function factorPPM(mes, anio, P) {
  if (!P?.ipc && P?.ipcProyectado === undefined) return { factor: 1.01, proyectados: [], aprox: true };
  const proyectados = [];
  let f = 1;
  for (let k = mes + 1; k <= 11; k++) {
    const v = P.ipc?.[mesKey(anio, k)];
    if (v === undefined || v === null) {
      proyectados.push(k);
      f *= 1 + n(P.ipcProyectado) / 100;
    } else f *= 1 + n(typeof v === "object" ? v.valor : v) / 100;
  }
  return { factor: Math.max(1, Math.round(f * 1000) / 1000), proyectados, aprox: false };
}

// ---------- Datos del ERP indexados por número de mes ----------
export function erpPorMes(X, anio) {
  if (!X) return null;
  const pick = (obj) => {
    const out = {};
    for (let m = 1; m <= 12; m++) {
      const v = obj?.[mesKey(anio, m)];
      if (v) out[m] = v;
    }
    return out;
  };
  const hon = pick(X.honorarios);
  const rem = pick(X.remuneraciones);
  const ven = pick(X.ventas);
  const com = pick(X.compras);
  return {
    hon, rem, ven, com,
    hay: {
      hon: Object.keys(hon).length > 0,
      rem: Object.keys(rem).length > 0,
      ven: Object.keys(ven).length > 0,
      com: Object.keys(com).length > 0,
      conta: (X.contabilidad?.asientos || 0) > 0,
    },
    hasta: X.hasta,
    generado: X.generado,
  };
}

// ¿El libro de honorarios del ERP cubre todos los meses en que el F29 informa retenciones?
export function honorariosERPCompletos(C, X, anio) {
  const e = erpPorMes(X, anio);
  if (!e?.hay.hon) return false;
  const conRet = (C?.f29 || []).filter((f) => f.declarado && f.anio === anio && n(f.codes?.["151"]) > 0);
  return conRet.every((f) => e.hon[f.mes]);
}

const tolerancia = (v) => Math.max(1000, Math.abs(v) * 0.01);
const comparar = (f29, erp) => {
  if (erp === null || erp === undefined) return { f29, erp: null, dif: null, ok: null };
  const dif = erp - f29;
  return { f29, erp, dif, ok: Math.abs(dif) <= tolerancia(f29) };
};

function conciliacionERP(decl, erp) {
  if (!erp) return null;
  const filas = decl.map((r) => {
    const c = r.f.codes || {};
    const v = erp.ven[r.mes];
    const k = erp.com[r.mes];
    const h = erp.hon[r.mes];
    const q = erp.rem[r.mes];
    const credF29 = n(c["520"]) + n(c["525"]) - n(c["528"]);
    let credERP = null;
    if (k) {
      // Según el origen del RCV, el IVA del activo fijo viene dentro o fuera del "IVA recuperable".
      const base = n(k.iva) + n(k.iva_uso_comun);
      const conAF = base + n(k.iva_activo_fijo);
      credERP = Math.abs(conAF - credF29) < Math.abs(base - credF29) ? conAF : base;
    }
    return {
      mes: r.mes,
      nombre: r.nombre,
      ventas: erp.hay.ven ? comparar(r.ventas, v ? n(v.neto) : null) : null,
      debito: erp.hay.ven ? comparar(r.debito, v ? n(v.iva) : null) : null,
      credito: erp.hay.com ? comparar(credF29, credERP) : null,
      ret: erp.hay.hon ? comparar(n(c["151"]), h ? n(h.retencion) : n(c["151"]) ? null : 0) : null,
      impUnico: erp.hay.rem ? comparar(n(c["48"]), q ? n(q.impuesto_unico) : n(c["48"]) ? null : 0) : null,
    };
  });
  const difs = (k) => filas.filter((f) => f[k] && f[k].ok === false).map((f) => f.nombre.toLowerCase());
  const sinDatos = (k) => filas.filter((f) => f[k] && f[k].ok === null).map((f) => f.nombre.toLowerCase());
  const resumen = {};
  for (const k of ["ventas", "debito", "credito", "ret", "impUnico"]) {
    const disponible = filas.some((f) => f[k]);
    resumen[k] = { disponible, difs: disponible ? difs(k) : [], sinDatos: disponible ? sinDatos(k) : [] };
  }
  return { filas, resumen };
}

// ---------- Sugerencias desde el ERP para el panel de parámetros ----------
export function sugerenciasERP(X, { anio, ultimoMes, gastosSel } = {}) {
  if (!X) return null;
  const e = erpPorMes(X, anio);
  const hastaMes = ultimoMes || +String(X.hasta || "").slice(5, 7) || 12;

  const honMeses = Object.keys(e.hon).map(Number).filter((m) => m <= hastaMes);
  const honorarios = {
    meses: honMeses.length,
    bruto: Math.round(honMeses.reduce((s, m) => s + n(e.hon[m].bruto), 0)),
    retencion: Math.round(honMeses.reduce((s, m) => s + n(e.hon[m].retencion), 0)),
    docs: honMeses.reduce((s, m) => s + n(e.hon[m].docs), 0),
  };

  const costoRem = (q) => n(q.haberes) + n(q.costo_empleador);
  const remMeses = Object.keys(e.rem).map(Number).sort((a, b) => a - b);
  const remAcum = remMeses.reduce((s, m) => s + costoRem(e.rem[m]), 0);
  const remUlt = remMeses.length ? remMeses[remMeses.length - 1] : 0;
  const remBase = remMeses.slice(-3);
  const remProm = remBase.length ? remBase.reduce((s, m) => s + costoRem(e.rem[m]), 0) / remBase.length : 0;
  const remuneraciones = {
    meses: remMeses.length,
    ultimoMes: remUlt,
    acumulado: Math.round(remAcum),
    anual: Math.round(remAcum + remProm * (12 - remUlt)),
    impuestoUnico: Math.round(remMeses.reduce((s, m) => s + n(e.rem[m].impuesto_unico), 0)),
  };

  const filas = X.contabilidad?.resultado || [];
  const acumHasta = (fila) =>
    Object.entries(fila.meses || {})
      .filter(([k]) => k.startsWith(`${anio}-`) && +k.slice(5, 7) <= hastaMes)
      .reduce((s, [, v]) => s + n(v), 0);
  const ingresos = filas.filter((f) => f.tipo === "ingreso");
  const operacionales = ingresos.filter((f) => (f.codigo || "").startsWith("4.1"));
  const ventasOp = operacionales.length ? operacionales : ingresos;
  const egresos = filas
    .filter((f) => f.tipo === "egreso")
    .map((f) => ({ codigo: f.codigo, nombre: f.nombre, categoria: f.categoria, acumulado: Math.round(acumHasta(f)) }))
    .filter((f) => f.acumulado !== 0);
  const seleccion = gastosSel || egresos.filter((f) => f.categoria === "sin_iva_probable").map((f) => f.codigo);
  const gastosAcum = egresos.filter((f) => seleccion.includes(f.codigo)).reduce((s, f) => s + f.acumulado, 0);
  const bal = X.contabilidad?.balance || {};
  const contabilidad = e.hay.conta
    ? {
        asientos: X.contabilidad.asientos,
        ventas: Math.round(ventasOp.reduce((s, f) => s + acumHasta(f), 0)),
        gastos: egresos.filter((f) => f.categoria !== "impuesto_renta").reduce((s, f) => s + f.acumulado, 0),
        caja: bal.caja?.total || 0,
        clientes: bal.clientes?.total || 0,
        activoFijoNeto: bal.activo_fijo?.total || 0,
        proveedores: bal.proveedores?.total || 0,
        prestamos: bal.prestamos?.total || 0,
        capitalPagado: bal.capital?.total || 0,
        balance: bal,
      }
    : null;

  return {
    hastaMes,
    hay: e.hay,
    honorarios,
    remuneraciones,
    gastos: { egresos, seleccion, acumulado: gastosAcum, anual: hastaMes ? Math.round((gastosAcum * 12) / hastaMes) : 0 },
    contabilidad,
  };
}

// ---------- Análisis principal ----------
export function analyze(C, T, P, X) {
  const corte = new Date(P.fechaCorte + "T12:00:00");
  const anio = +P.anio;
  const inicio = parseDMY(C.inicioActividades);
  const rk = regimenKey(C.regimen);
  const erp = erpPorMes(X, anio);
  const tgrRows = [
    ...(T?.morosa || []).map((r) => ({ ...r, estado: "morosa" })),
    ...(T?.noVencida || []).map((r) => ({ ...r, estado: "noVencida" })),
    ...(T?.convenio || []).map((r) => ({ ...r, estado: "convenio" })),
  ];
  const byFolio = Object.fromEntries(tgrRows.map((r) => [r.folio, r]));
  const f29ByPer = Object.fromEntries((C.f29 || []).map((f) => [f.periodo, f]));

  // ---------- Detalle mensual ----------
  const meses = [];
  for (let m = 1; m <= 12; m++) {
    const per = anio * 100 + m;
    const f = f29ByPer[per];
    const venc = vencF29(anio, m);
    const row = { mes: m, nombre: MES_NOMBRE[m - 1], periodo: per, venc, f };
    const antesInicio = inicio && new Date(anio, m, 0) < new Date(inicio.getFullYear(), inicio.getMonth(), 1);
    if (antesInicio) {
      row.estado = "antes";
      meses.push(row);
      continue;
    }
    if (f && f.declarado) {
      const c = f.codes || {};
      row.declarado = true;
      row.ventas = n(c["563"]) || Math.round(n(c["538"]) / IVA);
      row.debito = n(c["538"]);
      row.debFact = n(c["502"]);
      row.debBol = n(c["111"]);
      row.debNC = n(c["510"]);
      // Preferir 537 (total créditos); 511 queda como compatibilidad
      row.credMes = has(c["537"]) ? n(c["537"]) : (has(c["511"]) ? n(c["511"]) : Math.max(0, n(c["537"]) - n(c["504"])));
      row.remAnterior = n(c["504"]);
      row.remanente = n(c["77"]);
      row.comprasGiro = Math.round((n(c["520"]) - n(c["528"])) / IVA);
      row.comprasAF = Math.round(n(c["525"]) / IVA);
      row.ivaDet = n(c["89"]);
      row.postergado = n(c["755"]);
      row.ppm = n(c["62"]);
      const fp = factorPPM(m, anio, P);
      row.factorPPM = fp.factor;
      row.ppmReaj = Math.round(row.ppm * fp.factor);
      row.ipcProyectado = fp.proyectados.length > 0 && row.ppm > 0;
      // Código 115 = tasa PPM en puntos porcentuales (0.125 = 0,125%). Corrige informes ya parseados
      // con el bug del parser que leía "0.125" como miles chilenos → 125.
      row.tasaPPM = normalizeTasaPPM(has(c["115"]) ? +c["115"] : null);
      row.ret = n(c["151"]) + n(c["153"]);
      row.impUnico = n(c["48"]);
      row.totalF29 = n(c["91"]);
      row.fechaPres = parseDMY(f.fechaPresentacion);
      row.aTiempo = row.fechaPres ? row.fechaPres <= venc : null;
      row.rectificatoria = /rectific/i.test(f.tipoDecl || "") || !!f.corrigeFolio;
      row.folio = f.folio;
      const t = byFolio[f.folio];
      if (row.postergado > 0) {
        row.vencPost = vencPostergado(anio, m);
        if (t) row.pago = t.estado === "morosa" ? "moroso" : "postergadoVigente";
        else row.pago = row.vencPost < corte ? "pagado" : "postergadoVigente";
      } else if (t) row.pago = t.estado === "morosa" ? "moroso" : "porVencer";
      else row.pago = row.totalF29 > 0 ? (f.medioPago ? "pagado" : "revisar") : "sinPago";
      row.estado = row.aTiempo === false ? "tarde" : "ok";
      row.tgr = t || null;
    } else {
      row.declarado = false;
      if (venc < corte) row.estado = f ? "omitido" : "sinInfo";
      else if (new Date(anio, m, 0) < corte) row.estado = "plazo";
      else if (new Date(anio, m - 1, 1) <= corte) row.estado = "curso";
      else row.estado = "futuro";
    }
    meses.push(row);
  }
  const decl = meses.filter((r) => r.declarado);
  const sum = (k, arr = decl) => arr.reduce((s, r) => s + n(r[k]), 0);
  const tot = {
    ventas: sum("ventas"), debito: sum("debito"), credMes: sum("credMes"), comprasGiro: sum("comprasGiro"), comprasAF: sum("comprasAF"),
    ivaDet: sum("ivaDet"), postergado: sum("postergado"), ppm: sum("ppm"), ppmReaj: sum("ppmReaj"), ret: sum("ret"), impUnico: sum("impUnico"), totalF29: sum("totalF29"),
    debFact: sum("debFact"), debBol: sum("debBol"),
  };
  const ufValor = n(P.uf) || UF_RESPALDO;
  const intDiario = interesDiario(P);
  const reajPPM = {
    aprox: factorPPM(1, anio, P).aprox,
    conProyeccion: decl.some((r) => r.ipcProyectado),
    monto: tot.ppmReaj - tot.ppm,
  };
  const lastDecl = decl.length ? decl[decl.length - 1] : null;
  const ultimoMes = lastDecl ? lastDecl.mes : 0;
  const remanenteActual = lastDecl ? lastDecl.remanente : 0;

  // ---------- Contrato ERP v1.0 y validaciones previas a la emisión ----------
  const factores = {};
  for (let m = 1; m <= 12; m++) {
    const fp = factorPPM(m, anio, P);
    if (!fp.aprox) factores[mesKey(anio, m)] = fp.factor;
  }
  const E = construirE(X, P, factores);
  const validacionesM = validaciones({ C, T, P, E, X, decl, ultimoMes, rk });
  const val = Object.fromEntries(validacionesM.map((v) => [v.id, v]));
  const bloqueos = validacionesM.filter((v) => v.estado === "BLOQUEO");
  const advertencias = validacionesM.filter((v) => v.estado === "ADVERTENCIA");

  // ---------- Promedios y proyección ----------
  const baseProm = decl
    .filter((r) => !(inicio && inicio.getFullYear() === anio && r.mes === inicio.getMonth() + 1 && inicio.getDate() > 5))
    .slice(-Math.max(1, +P.mesesPromedio || 6));
  const avg = (k) => (baseProm.length ? sum(k, baseProm) / baseProm.length : 0);
  const restantes = 12 - ultimoMes;
  const tasaRet = (+P.tasaRetHon || 15.25) / 100;
  // Regla de conflicto: el libro reemplaza la estimación del F29 solo si pasa V01.
  const honorariosPedidoERP = P.fuenteHonorarios === "erp" && !!erp?.hay.hon;
  const honorariosERP = honorariosPedidoERP && (!val.V01 || val.V01.estado === "OK");
  const honorariosConflicto = honorariosPedidoERP && !honorariosERP;
  const honBrutoMes = (m) => n(erp?.hon[m]?.bruto);
  const honorarios = honorariosERP
    ? Math.round(decl.reduce((s, r) => s + honBrutoMes(r.mes), 0))
    : Math.round(tot.ret / tasaRet);
  const prom = { ventas: avg("ventas"), comprasGiro: avg("comprasGiro"), ret: avg("ret"), ppm: avg("ppm"), ivaDet: avg("ivaDet"), totalF29: avg("totalF29") };
  prom.honorarios = honorariosERP
    ? (baseProm.length ? baseProm.reduce((s, r) => s + honBrutoMes(r.mes), 0) / baseProm.length : 0)
    : prom.ret / tasaRet;
  const proy = {
    restantes, mesesBase: baseProm.length,
    ventas: tot.ventas + prom.ventas * restantes,
    comprasGiro: tot.comprasGiro + prom.comprasGiro * restantes,
    comprasAF: tot.comprasAF,
    honorarios: honorarios + prom.honorarios * restantes,
  };
  const ppmFuturo = prom.ventas * restantes * (n(P.tasaPPMFutura) / 100);
  proy.ppmTotal = tot.ppm + ppmFuturo;
  // Pro Pyme General rebaja gastos pagados: si el asesor confirma que las facturas siguen impagas, no se descuentan.
  const provImpagos = E?.contabilidad?.saldos?.proveedores;
  proy.comprasImpagas =
    P.excluirImpagas && rk === "14D3" && has(provImpagos) ? Math.min(proy.comprasGiro, Math.max(0, Math.round(n(provImpagos) / (1 + IVA)))) : 0;

  const escenario = (mej) => {
    const gastosSinIVA = n(P.gastosSinIVA) + n(P.remuneraciones);
    const af = P.afComoGasto ? proy.comprasAF + (mej ? n(P.compraAFPlan) : 0) : 0;
    const resultado = proy.ventas - (proy.comprasGiro - proy.comprasImpagas) - proy.honorarios - gastosSinIVA - af - (mej ? n(P.gastosAnticipados) : 0);
    const rliAntes = resultado + n(P.agregados) - n(P.deducciones);
    let ded14E = 0;
    if (mej && rk === "14D3" && rliAntes > 0) {
      const base = Math.max(0, rliAntes - n(P.retirosEstimados));
      ded14E = Math.min((base * Math.min(50, n(P.pct14E))) / 100, 5000 * ufValor);
    }
    const rli = rliAntes - ded14E;
    const idpc = Math.max(0, rli) * (n(P.tasaIDPC) / 100);
    const ppmAct = tot.ppmReaj;
    const ppmFut = ppmFuturo;
    const saldo = idpc - ppmAct - ppmFut - n(P.otrosCreditos);
    return {
      ventas: proy.ventas, compras: proy.comprasGiro - proy.comprasImpagas, honorarios: proy.honorarios, gastosSinIVA, af,
      gastosAnt: mej ? n(P.gastosAnticipados) : 0, resultado, agregados: n(P.agregados), deducciones: n(P.deducciones),
      ded14E, rli, idpc, ppmAct, ppmFut, otrosCreditos: n(P.otrosCreditos), saldo,
    };
  };
  const esc = { actual: escenario(false), mejoras: escenario(true) };
  esc.ahorro = esc.actual.idpc - esc.mejoras.idpc;
  const resultadoAFecha =
    tot.ventas - tot.comprasGiro - honorarios - (P.afComoGasto ? tot.comprasAF : 0) - (n(P.gastosSinIVA) + n(P.remuneraciones)) * (ultimoMes / 12);

  // ---------- PPM ----------
  const tasas = decl.filter((r) => r.tasaPPM !== null).map((r) => r.tasaPPM);
  const tasasDistintas = [...new Set(tasas)];
  const ppmLegalMonto = decl.reduce((s, r) => s + (r.ventas * n(P.ppmLegal)) / 100, 0);
  const ppmSobreLegal = tot.ppm - ppmLegalMonto;
  const bajoLegal = decl.filter((r) => r.tasaPPM !== null && r.tasaPPM < n(P.ppmLegal));
  const faltaCubrir = esc.actual.idpc - tot.ppmReaj - n(P.otrosCreditos);
  const ventasRest = prom.ventas * restantes;
  const mesesHastaAbril = Math.max(1, (anio + 1 - corte.getFullYear()) * 12 + 4 - (corte.getMonth() + 1));
  const reservaMensual = Math.max(0, esc.actual.saldo) / mesesHastaAbril;
  const tasaNecesaria = ventasRest > 0 ? Math.max(n(P.ppmLegal), (faltaCubrir / ventasRest) * 100) : null;

  // ---------- Remanente: continuidad ----------
  const remChecks = [];
  for (let i = 1; i < decl.length; i++) {
    const a = decl[i - 1];
    const b = decl[i];
    if (b.mes !== a.mes + 1) continue;
    if (a.remanente > 0 || b.remAnterior > 0) {
      const ratio = a.remanente ? b.remAnterior / a.remanente : null;
      remChecks.push({ de: a.nombre, a: b.nombre, cierre: a.remanente, apertura: b.remAnterior, ok: ratio !== null && ratio >= 0.98 && ratio <= 1.05 });
    }
  }

  // ---------- TGR ----------
  const dias = (d) => Math.round((d - corte) / DAY);
  const tgr = {
    disponible: !!T, morosa: T?.totalMorosa || 0, noVencida: T?.totalNoVencida || 0, convenio: T?.totalConvenio || 0,
    filas: tgrRows.map((r) => {
      const mesOrigen = meses.find((x) => x.folio === r.folio);
      const recargos = r.reajuste + r.interes + r.multa;
      return {
        ...r,
        origen: mesOrigen ? `${mesOrigen.nombre} ${anio}` : r.formulario === "21" ? "F29 (IVA)" : `Formulario ${r.formulario}`,
        esPostergado: !!(mesOrigen && mesOrigen.postergado),
        dias: r.venc ? dias(r.venc) : null,
        recargos,
        pctRecargo: r.neta ? recargos / r.neta : 0,
        interesDiario: (r.neta + r.reajuste) * intDiario,
      };
    }),
    liquidadaAl: T?.liquidadaAl, emision: T?.emision, codigo: T?.codigo,
    rutCoincide: T ? (T.rut || "").replace(/\./g, "").toUpperCase() === (C.rut || "").replace(/\./g, "").toUpperCase() : null,
  };
  tgr.periodosMorososIVA = tgr.filas.filter((r) => r.estado === "morosa" && r.formulario === "21").length;
  tgr.recargosMorosa = tgr.filas.filter((r) => r.estado === "morosa").reduce((s, r) => s + r.recargos, 0);
  tgr.interesDiario = tgr.filas.filter((r) => r.estado === "morosa").reduce((s, r) => s + r.interesDiario, 0);

  // ---------- Caja próximos 30 días ----------
  const lim30 = new Date(corte.getTime() + 31 * DAY);
  const caja = [];
  tgr.filas.filter((r) => r.estado === "morosa").forEach((r) => caja.push({ concepto: `Deuda morosa TGR (${r.origen.toLowerCase()})`, fecha: "Inmediato", monto: r.total, tipo: "rojo" }));
  tgr.filas
    .filter((r) => r.estado === "noVencida" && r.venc && r.venc <= lim30)
    .forEach((r) => caja.push({ concepto: `${r.esPostergado ? "IVA postergado" : "Giro por vencer"} (${r.origen.toLowerCase()})`, fecha: fmtDate(r.venc), monto: r.total, tipo: "amarillo" }));
  meses
    .filter((r) => r.estado === "plazo" && r.venc <= lim30)
    .forEach((r) =>
      caja.push({
        concepto: `F29 de ${r.nombre.toLowerCase()} (estimado: IVA + PPM + retenciones)`,
        fecha: fmtDate(r.venc),
        monto: Math.round(prom.ivaDet + (prom.ventas * n(P.tasaPPMFutura)) / 100 + prom.ret),
        tipo: "amarillo",
        estimado: true,
      })
    );
  const cajaTotal = caja.reduce((s, r) => s + r.monto, 0);

  // ---------- F22 / años ----------
  const atPrimero = inicio ? inicio.getFullYear() + 1 : null;
  const f22Omitidos = (C.f22 || []).filter((f) => !f.declarado && atPrimero && f.at >= atPrimero && new Date(f.at, 4, 1) < corte);

  // ---------- Comparación con el año anterior (mismos meses) ----------
  const anioAnt = anio - 1;
  const f29Ant = Object.fromEntries((C.f29 || []).filter((f) => f.declarado && f.anio === anioAnt).map((f) => [f.mes, f]));
  const comunes = decl.filter((r) => f29Ant[r.mes]);
  let comp = null;
  if (comunes.length) {
    const cAnt = (fn) => comunes.reduce((s, r) => s + fn(f29Ant[r.mes].codes || {}), 0);
    const fila = (actual, anterior) => ({ actual, anterior, var: anterior ? (actual - anterior) / Math.abs(anterior) : null });
    comp = {
      anio: anioAnt,
      meses: comunes.length,
      rango: comunes.length === 1 ? comunes[0].nombre.toLowerCase() : `${comunes[0].nombre.toLowerCase()} a ${comunes[comunes.length - 1].nombre.toLowerCase()}`,
      filas: [
        ["Ventas netas", fila(sum("ventas", comunes), cAnt((c) => n(c["563"]) || Math.round(n(c["538"]) / IVA)))],
        ["Compras netas del giro", fila(sum("comprasGiro", comunes), cAnt((c) => Math.round((n(c["520"]) - n(c["528"])) / IVA)))],
        ["IVA débito", fila(sum("debito", comunes), cAnt((c) => n(c["538"])))],
        ["PPM pagados", fila(sum("ppm", comunes), cAnt((c) => n(c["62"])))],
        ["Retenciones", fila(sum("ret", comunes), cAnt((c) => n(c["151"]) + n(c["153"])))],
        ["Total pagado en F29", fila(sum("totalF29", comunes), cAnt((c) => n(c["91"])))],
      ],
    };
    comp.ventas = comp.filas[0][1];
  }
  const f22Decl = (C.f22 || []).filter((f) => f.declarado && f.codes).sort((a, b) => b.at - a.at);
  const f22u = f22Decl.find((f) => f.at === anio) || f22Decl[0];
  const f22Prev = f22u
    ? { at: f22u.at, folio: f22u.folio, resultado: f22u.codes["305"], devolucion: f22u.codes["87"], aPagar: f22u.codes["91"] }
    : null;

  // ---------- Conciliación con el ERP ----------
  const concERP = conciliacionERP(decl, erp);

  // ---------- Semáforo ----------
  const omitidos = meses.filter((r) => r.estado === "omitido");
  const tardes = meses.filter((r) => r.estado === "tarde");
  const enPlazo = meses.filter((r) => r.estado === "plazo");
  const sem = [];
  sem.push({
    tema: "Declaraciones mensuales (F29)",
    estado: omitidos.length ? "rojo" : tardes.length ? "amarillo" : "verde",
    texto: omitidos.length
      ? `Faltan ${omitidos.length} declaraciones: ${omitidos.map((r) => r.nombre.toLowerCase()).join(", ")}.`
      : tardes.length
        ? `${decl.length} presentadas; ${tardes.length} fuera de plazo (${tardes.map((r) => r.nombre.toLowerCase()).join(", ")}).`
        : `${decl.length} de ${decl.length} presentadas a tiempo.${enPlazo.length ? ` El de ${enPlazo.map((r) => r.nombre.toLowerCase()).join(", ")} vence el ${fmtDate(enPlazo[0].venc)}.` : ""}`,
  });
  if (T) {
    sem.push({
      tema: "Deuda con Tesorería (TGR)",
      estado: tgr.morosa > 0 ? "rojo" : tgr.noVencida > 0 ? "amarillo" : "verde",
      texto:
        tgr.morosa > 0
          ? `Deuda vencida de ${clp(tgr.morosa)}, que ya incluye ${clp(tgr.recargosMorosa)} en recargos. Sigue creciendo cada día.`
          : tgr.noVencida > 0
            ? `Sin deuda vencida. Tiene ${clp(tgr.noVencida)} por pagar antes de su vencimiento.`
            : "Sin deudas registradas en Tesorería.",
    });
  } else sem.push({ tema: "Deuda con Tesorería (TGR)", estado: "gris", texto: "Falta cargar el certificado de deuda de la TGR." });
  const postVig = meses.filter((r) => r.postergado > 0 && r.pago === "postergadoVigente");
  const postMor = meses.filter((r) => r.postergado > 0 && r.pago === "moroso");
  sem.push({
    tema: "IVA postergado",
    estado: postMor.length ? "rojo" : postVig.length ? "amarillo" : "verde",
    texto: postMor.length
      ? `No se pagó el IVA postergado de ${postMor.map((r) => r.nombre.toLowerCase()).join(", ")}.${postVig.length ? ` Además vence el de ${postVig.map((r) => r.nombre.toLowerCase()).join(", ")} (${clp(sum("postergado", postVig))}) el ${fmtDate(postVig[0].vencPost)}.` : ""}`
      : postVig.length
        ? `${clp(sum("postergado", postVig))} por pagar el ${fmtDate(postVig[0].vencPost)}.`
        : "No tiene IVA postergado pendiente.",
  });
  sem.push({
    tema: "Pagos provisionales (PPM)",
    estado: bajoLegal.length ? "rojo" : tasasDistintas.length > 1 || (faltaCubrir > 0 && tasaNecesaria > n(P.tasaPPMFutura) + 0.05) ? "amarillo" : "verde",
    texto: bajoLegal.length
      ? `En ${bajoLegal.length} meses la tasa quedó bajo el mínimo legal (${fmtPct(P.ppmLegal)}).`
      : `${clp(tot.ppm)} abonados al impuesto de abril.${tasasDistintas.length > 1 ? ` La tasa cambió entre meses (${tasasDistintas.map((t) => fmtPct(t)).join(", ")}).` : ""}`,
  });
  sem.push({
    tema: "Resultado del ejercicio",
    estado: resultadoAFecha >= 0 ? "verde" : "amarillo",
    texto: `${resultadoAFecha >= 0 ? "Utilidad" : "Pérdida"} tributaria estimada de ${clp(Math.abs(resultadoAFecha))} entre enero y ${(lastDecl?.nombre || "").toLowerCase()}.`,
  });
  sem.push({
    tema: `Impuesto a la renta proyectado (abril ${anio + 1})`,
    estado: esc.actual.saldo > 0 ? "amarillo" : "verde",
    texto:
      esc.actual.saldo > 0
        ? `Estimamos pagar ${clp(esc.actual.saldo)} en abril${esc.ahorro > 0 ? `; con las mejoras propuestas bajaría a ${clp(Math.max(0, esc.mejoras.saldo))}` : ""}.`
        : `Estimamos una devolución de ${clp(-esc.actual.saldo)} en abril.`,
  });
  sem.push({
    tema: "Situación ante el SII",
    estado: C.anotaciones.length || f22Omitidos.length ? "rojo" : "verde",
    texto: C.anotaciones.length
      ? `Registra ${C.anotaciones.length} anotación(es) vigente(s): revisar de inmediato.`
      : f22Omitidos.length
        ? `Faltan declaraciones de renta: AT ${f22Omitidos.map((f) => f.at).join(", ")}.`
        : "Sin anotaciones vigentes ni declaraciones de renta pendientes.",
  });
  if (concERP) {
    const r = concERP.resumen;
    const difVentas = [...new Set([...r.ventas.difs, ...r.debito.difs])];
    const difCompras = r.credito.difs;
    if (r.ventas.disponible || r.credito.disponible) {
      sem.push({
        tema: "Registro de compras y ventas vs F29",
        estado: difVentas.length || difCompras.length ? "amarillo" : "verde",
        texto:
          difVentas.length || difCompras.length
            ? `Hay diferencias por revisar${difVentas.length ? ` en ventas (${difVentas.join(", ")})` : ""}${difCompras.length ? `${difVentas.length ? " y" : ""} en compras (${difCompras.join(", ")})` : ""}.`
            : "Los registros de compras y ventas cuadran con lo declarado en los F29.",
      });
    }
  }

  // ---------- Observaciones (hallazgos) ----------
  const obs = [];
  postMor.forEach((r) => {
    const t = tgr.filas.find((x) => x.folio === r.folio);
    obs.push({
      p: "Alta",
      t: `No se pagó el IVA postergado de ${r.nombre.toLowerCase()} (${clp(r.postergado)}), que venció el ${fmtDate(r.vencPost)}.`,
      i: `La TGR ya cobra ${clp(t.recargos)} en reajuste, intereses y multa (${fmtPct(t.pctRecargo * 100, 1)} sobre el impuesto). Los intereses suman cerca de ${clp(t.interesDiario)} por día.`,
      a: "Pagar o firmar un convenio de pago con la TGR.",
    });
  });
  postVig.forEach((r) =>
    obs.push({
      p: "Alta",
      t: `Tiene ${clp(r.postergado)} de IVA postergado de ${r.nombre.toLowerCase()}, que vence el ${fmtDate(r.vencPost)}.`,
      i: `Si no se paga, se suma a la deuda morosa.${tgr.periodosMorososIVA >= 1 ? ` Con ${tgr.periodosMorososIVA + 1} períodos de IVA impagos en 12 meses, la empresa se acerca al límite de morosidad reiterada (3 períodos) y perdería el derecho a postergar el IVA.` : ""}`,
      a: "Reservar el dinero desde hoy.",
    })
  );
  omitidos.forEach((r) =>
    obs.push({ p: "Alta", t: `No aparece el F29 de ${r.nombre.toLowerCase()} ${anio}.`, i: "Una declaración omitida genera multas y el SII puede fiscalizar.", a: "Confirmar si se declaró en papel o presentarla cuanto antes." })
  );
  tardes.forEach((r) =>
    obs.push({
      p: "Media",
      t: `El F29 de ${r.nombre.toLowerCase()} se presentó el ${fmtDate(r.fechaPres)}, después del plazo (${fmtDate(r.venc)}).`,
      i: "Pudo generar multa e intereses, además de impedir la postergación del IVA de ese mes.",
      a: "Revisar si quedaron recargos pendientes.",
    })
  );
  if (tasasDistintas.length > 1) {
    obs.push({
      p: "Media",
      t: `La tasa de PPM cambió entre meses: ${decl.filter((r) => r.tasaPPM !== null).map((r) => `${r.nombre.slice(0, 3)} ${fmtPct(r.tasaPPM)}`).join(" · ")}.`,
      i: `La tasa mínima legal es ${fmtPct(P.ppmLegal)}. Ha pagado ${clp(ppmSobreLegal)} por sobre el mínimo. No es una pérdida, porque se descuenta en abril, pero una tasa irregular dificulta planificar la caja.`,
      a: "Fijar una sola tasa según el impuesto proyectado (ver la sección de renta).",
    });
  } else if (ppmSobreLegal > 1000 && tasasDistintas.length === 1) {
    obs.push({
      p: "Baja",
      t: `La tasa de PPM (${fmtPct(tasasDistintas[0])}) está sobre el mínimo legal (${fmtPct(P.ppmLegal)}).`,
      i: `Ha pagado ${clp(ppmSobreLegal)} por sobre el mínimo. Se descuenta en abril.`,
      a: "Confirmar que la tasa sea coherente con el impuesto proyectado.",
    });
  }
  bajoLegal.forEach((r) =>
    obs.push({ p: "Alta", t: `En ${r.nombre.toLowerCase()} la tasa de PPM (${fmtPct(r.tasaPPM)}) quedó bajo el mínimo legal.`, i: "El SII puede exigir la diferencia con recargos.", a: "Rectificar el F29." })
  );
  remChecks
    .filter((x) => !x.ok)
    .forEach((x) =>
      obs.push({
        p: "Media",
        t: `El remanente de crédito no cuadra entre ${x.de.toLowerCase()} (${clp(x.cierre)}) y ${x.a.toLowerCase()} (${clp(x.apertura)}).`,
        i: "Una diferencia en el arrastre del remanente es un punto típico de revisión del SII.",
        a: "Conciliar el registro de compras y ventas con los F29.",
      })
    );
  if (concERP) {
    const r = concERP.resumen;
    const difVentas = [...new Set([...r.ventas.difs, ...r.debito.difs])];
    if (difVentas.length) {
      obs.push({
        p: "Media",
        t: `Las ventas registradas no cuadran con las declaradas en el F29 de ${difVentas.join(", ")}.`,
        i: "Diferencias entre el registro de ventas y el F29 son un punto típico de fiscalización del SII.",
        a: "Revisar documentos faltantes, duplicados o notas de crédito y rectificar si corresponde.",
      });
    }
    if (r.credito.difs.length) {
      obs.push({
        p: "Media",
        t: `El IVA crédito del registro de compras no cuadra con el F29 de ${r.credito.difs.join(", ")}.`,
        i: "Un crédito declarado de más puede ser rechazado por el SII con multas e intereses; uno de menos es impuesto pagado en exceso.",
        a: "Conciliar el registro de compras con los F29 y rectificar si corresponde.",
      });
    }
  }
  if (tot.ret > 0) {
    obs.push({
      p: "Media",
      t: `Retuvo ${clp(tot.ret)} por boletas de honorarios (${honorariosERP ? `${clp(honorarios)} brutos según el libro de honorarios` : `unos ${clp(honorarios)} brutos`}).`,
      i: "Obliga a presentar la DJ 1879 en marzo y a entregar los certificados a los prestadores.",
      a: "Validar el detalle de las boletas recibidas antes de diciembre.",
    });
  }
  if (tot.comprasAF > 0) {
    obs.push({
      p: "Media",
      t: `Compró activo fijo por cerca de ${clp(tot.comprasAF)} netos (${meses.filter((r) => r.comprasAF > 0).map((r) => r.nombre.toLowerCase()).join(", ")}).`,
      i: rk === "14D3" ? "En Pro Pyme se puede rebajar completo como gasto el mismo año (depreciación instantánea), si está pagado y en uso." : "Debe registrarse y depreciarse según su vida útil.",
      a: "Confirmar facturas, pago y destino de los bienes.",
    });
  }
  const pre = (C.f29 || []).filter((f) => f.declarado && inicio && new Date(f.anio, f.mes, 0) < new Date(inicio.getFullYear(), inicio.getMonth(), 1));
  pre.forEach((f) =>
    obs.push({
      p: "Baja",
      t: `Se presentó un F29 de ${MES_NOMBRE[f.mes - 1].toLowerCase()} ${f.anio}, antes del inicio de actividades (${C.inicioActividades}).`,
      i: "No tiene montos, así que no afecta, pero no correspondía presentarlo.",
      a: "Solo dejarlo registrado.",
    })
  );
  if (tot.impUnico === 0 && n(P.remuneraciones) === 0) {
    obs.push({
      p: "Baja",
      t: "Los F29 no registran impuesto único de trabajadores.",
      i: "Puede ser que no haya trabajadores contratados o que sus sueldos no alcancen a pagar impuesto. Si hay sueldos, deben incluirse en la proyección como gasto.",
      a: "Confirmar si hay remuneraciones y su monto.",
    });
  }
  if (C.anotaciones.length) obs.unshift({ p: "Alta", t: "El SII registra anotaciones vigentes.", i: C.anotaciones.join(" · "), a: "Revisar y regularizar en sii.cl." });

  // ---------- Oportunidades ----------
  const opp = [];
  if (tgr.morosa > 0) opp.push({ t: "Pagar hoy la deuda morosa o firmar un convenio con la TGR", b: Math.round(tgr.interesDiario * 30), bTxt: "Intereses de 30 días (aprox.)", q: "Decidir si paga al contado o en cuotas", plazo: "Inmediato" });
  if (rk === "14D3" && esc.mejoras.ded14E > 0) {
    opp.push({
      t: "Mantener las utilidades en la empresa (incentivo al ahorro, art. 14 letra E)",
      b: Math.round((esc.mejoras.ded14E * n(P.tasaIDPC)) / 100),
      bTxt: `Rebaja de la base del ${fmtPct(P.pct14E, 0)} de la utilidad no retirada`,
      q: "Confirmar cuánto piensan retirar los socios",
      plazo: `31-12-${anio}`,
    });
  }
  opp.push({ t: "Adelantar antes del 31-12 los gastos y compras que ya tenían planificados", b: Math.round((1000000 * n(P.tasaIDPC)) / 100), bTxt: "Ahorro por cada $1.000.000 pagado", q: "Lista de gastos o equipos que necesita", plazo: `31-12-${anio}` });
  if (esc.actual.saldo > 0) {
    if (tasaNecesaria !== null && tasaNecesaria <= 3 && Math.abs(tasaNecesaria - n(P.tasaPPMFutura)) > 0.05) {
      opp.push({ t: `Subir la tasa de PPM al ${fmtPct(tasaNecesaria, 2)} en los meses que quedan`, b: null, bTxt: "Evita un pago grande en abril", q: "Aprobar la tasa", plazo: "Próximo F29" });
    } else {
      opp.push({ t: `Apartar ${clp(reservaMensual)} al mes desde ahora hasta abril para el impuesto a la renta`, b: null, bTxt: "Evita un pago grande e inesperado en abril", q: "Abrir una cuenta o fondo de reserva", plazo: "Desde este mes" });
    }
  } else if (esc.actual.saldo < -50000) {
    opp.push({ t: `Bajar la tasa de PPM al mínimo legal (${fmtPct(P.ppmLegal)})`, b: null, bTxt: "Libera caja mensual: hoy se pagan PPM de más", q: "Aprobar la tasa", plazo: "Próximo F29" });
  }
  if (rk === "14D3" && (C.socios || []).length) {
    opp.push({ t: `Evaluar el régimen Pro Pyme Transparente (14 D N°8) para el año ${anio + 1}`, b: null, bTxt: "La empresa no paga primera categoría; los socios tributan según su tramo", q: "Otras rentas personales de cada socio", plazo: `30-04-${anio + 1}` });
  }
  if (tot.comprasAF > 0 && rk === "14D3") {
    opp.push({ t: "Respaldar el activo fijo para rebajarlo completo como gasto", b: Math.round((tot.comprasAF * n(P.tasaIDPC)) / 100), bTxt: "Impuesto que evita (ya incluido en la proyección)", q: "Facturas y comprobantes de pago", plazo: `31-12-${anio}` });
  }

  // ---------- Calendario ----------
  const cal = [];
  tgr.filas.filter((r) => r.estado === "morosa").forEach((r) => cal.push({ d: corte, f: "Inmediato", q: `Pagar o firmar convenio por la deuda morosa (${clp(r.total)})`, r: "Cliente", u: true }));
  meses
    .filter((r) => ["plazo", "curso", "futuro"].includes(r.estado))
    .forEach((r) => {
      if (r.venc >= corte) cal.push({ d: r.venc, f: fmtDate(r.venc), q: `F29 de ${r.nombre.toLowerCase()} (IVA, PPM y retenciones)`, r: "Asesor + cliente" });
    });
  tgr.filas
    .filter((r) => r.estado === "noVencida")
    .forEach((r) => cal.push({ d: r.venc, f: fmtDate(r.venc), q: `Pago ${r.esPostergado ? "IVA postergado" : "giro"} de ${r.origen.toLowerCase()} (${clp(r.total)})`, r: "Cliente", u: true }));
  cal.push({ d: new Date(anio, 11, 31), f: `31-12-${anio}`, q: "Cierre del año comercial: último día para aplicar las mejoras", r: "Cliente + asesor" });
  cal.push({ d: new Date(anio + 1, 2, 1), f: `Marzo ${anio + 1}`, q: `Declaraciones juradas anuales${tot.ret > 0 ? " (incluye DJ 1879 por honorarios)" : ""}. Confirmar fechas en el calendario del SII.`, r: "Asesor" });
  if (rk === "14D3") cal.push({ d: new Date(anio + 1, 3, 30), f: `30-04-${anio + 1}`, q: "Último día para cambiar de régimen Pro Pyme (aplica a ese mismo año)", r: "Cliente + asesor" });
  cal.push({ d: new Date(anio + 1, 3, 30), f: `Abril ${anio + 1}`, q: `Declaración de renta F22, año tributario ${anio + 1}`, r: "Asesor" });
  cal.sort((a, b) => (a.d || 0) - (b.d || 0));

  // ---------- Checklist documentos ----------
  const check = [
    "Cartolas bancarias de enero a la fecha",
    tot.comprasAF > 0 ? "Facturas y comprobantes de pago del activo fijo" : null,
    tot.ret > 0 ? "Detalle de boletas de honorarios recibidas (para la DJ 1879)" : null,
    "Retiros o dividendos que hayan recibido o piensen recibir los socios",
    "Gastos pagados sin factura (arriendos, cotizaciones, comisiones bancarias, seguros, patente)",
    "Remuneraciones y liquidaciones de sueldo, si hay trabajadores",
    "Préstamos, leasing o créditos vigentes (tabla de pagos)",
    "Capital suscrito y pagado por cada socio (aportes efectivos)",
    "Facturas emitidas que aún no le pagan (cuentas por cobrar)",
  ].filter(Boolean);

  // ---------- Contabilidad opcional ----------
  const cont = ["caja", "clientes", "activoFijoNeto", "proveedores", "prestamos", "capitalPagado", "ventasContables", "gastosContables"].some((k) => has(P[k]) && P[k] !== "");
  let conc = null;
  if (has(P.ventasContables) && P.ventasContables !== "") conc = { contable: n(P.ventasContables), f29: tot.ventas, dif: n(P.ventasContables) - tot.ventas };

  // ---------- Lectura automática ----------
  const lect = [];
  if (tot.ventas > 0) {
    const costo = tot.comprasGiro + honorarios;
    lect.push(`Por cada $100 que vende, gasta cerca de $${Math.round((costo / tot.ventas) * 100)} en compras con factura y honorarios (sin contar sueldos ni gastos sin factura).`);
    const best = decl.reduce((a, b) => (b.ventas > a.ventas ? b : a), decl[0]);
    lect.push(`Su mejor mes fue ${best.nombre.toLowerCase()} (${clp(best.ventas)}). Promedio mensual reciente: ${clp(prom.ventas)}.`);
    if (tot.debFact + tot.debBol > 0) lect.push(`El ${Math.round((tot.debBol / (tot.debFact + tot.debBol)) * 100)}% de sus ventas fue con boleta y el resto con factura.`);
    if (comp && comp.ventas.var !== null && Math.abs(comp.ventas.var) >= 0.005) {
      lect.push(
        `${comp.meses === 1 ? "En" : "De"} ${comp.rango} vendió ${clp(comp.ventas.actual)}, un ${fmtPct(Math.abs(comp.ventas.var) * 100, 1)} ${comp.ventas.var > 0 ? "más" : "menos"} que en los mismos meses de ${comp.anio}.`
      );
    }
  }
  const frase = buildFrase({ tgr, esc, omitidos });

  return {
    C, T, P, X, rk, corte, anio, meses, decl, tot, prom, proy, esc, honorarios, honorariosERP, ultimoMes, remanenteActual, resultadoAFecha,
    ppm: { reservaMensual, mesesHastaAbril, tasasDistintas, ppmLegalMonto, ppmSobreLegal, tasaNecesaria, faltaCubrir, ppmFuturo },
    remChecks, tgr, caja, cajaTotal, sem, obs, opp, cal, check, cont, conc, concERP, lect, frase, f22Omitidos,
    comp, f22Prev, ufValor, intDiario, reajPPM,
    E, validaciones: validacionesM, bloqueos, advertencias, honorariosConflicto,
  };
}

function buildFrase({ tgr, esc, omitidos }) {
  const partes = [];
  if (tgr.morosa > 0) partes.push(`Su empresa tiene una deuda vencida de ${clp(tgr.morosa)} con Tesorería que conviene resolver de inmediato`);
  else if (omitidos.length) partes.push(`Hay ${omitidos.length} declaraciones mensuales pendientes que se deben regularizar`);
  else partes.push("Su empresa está al día con sus declaraciones mensuales");
  if (esc.actual.saldo > 0) {
    partes.push(`proyectamos un impuesto a la renta de ${clp(esc.actual.saldo)} en abril${esc.ahorro > 0 ? `, que podemos bajar a ${clp(Math.max(0, esc.mejoras.saldo))} con las mejoras de este informe` : ""}`);
  } else partes.push(`proyectamos una devolución de ${clp(-esc.actual.saldo)} en abril`);
  return partes.join("; ") + ".";
}

// Resumen compacto que se guarda con el informe para mostrar en el historial.
export function resumenInforme(M) {
  const rojos = M.sem.filter((s) => s.estado === "rojo").length;
  const amarillos = M.sem.filter((s) => s.estado === "amarillo").length;
  return {
    ventas: Math.round(M.tot.ventas),
    resultado: Math.round(M.resultadoAFecha),
    renta: Math.round(M.esc.actual.saldo),
    rentaConMejoras: Math.round(M.esc.mejoras.saldo),
    deudaTGR: M.tgr.disponible ? Math.round(M.tgr.morosa + M.tgr.noVencida) : null,
    morosa: Math.round(M.tgr.morosa),
    rojos,
    amarillos,
    ultimoMes: M.ultimoMes,
    observaciones: M.obs.length,
    obsAltas: M.obs.filter((o) => o.p === "Alta").length,
    semaforo: M.sem.map((s) => ({ tema: s.tema, estado: s.estado, texto: s.texto })),
    proximo: proximoHito(M),
    frase: M.frase,
    bloqueos: (M.bloqueos || []).map((v) => ({ id: v.id, control: v.control })),
    advertencias: (M.advertencias || []).length,
  };
}

// Primer compromiso del calendario desde la fecha de corte (lo urgente primero).
function proximoHito(M) {
  const urg = M.cal.find((c) => c.u && c.f === "Inmediato");
  const sig = urg || M.cal.find((c) => c.d && c.d >= M.corte);
  if (!sig) return null;
  const d = sig.d instanceof Date && !isNaN(sig.d) ? sig.d : null;
  return {
    fecha: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : null,
    etiqueta: sig.f,
    que: sig.q,
  };
}
